<script lang="ts">
	import { onDestroy } from 'svelte';
	// Formating
	import { format } from 'svelte-i18n';
	// Helpers
	import { devices_status } from 'src/stores/device-runtime/status';
	import { devices } from 'src/stores/lists/devices';
	import { real_id } from 'src/helpers/interface_helpers';
	import { deriveRoomName } from 'src/helpers/service_rooms_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { sortBy } from 'src/components/Lists/list-helpers';
	import { postReq } from 'src/services/http';
	import { is_gen_2, isEM } from 'src/helpers/interface_helpers';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';
	import {
		getNetMeteringPeriods,
		isSupportFWNetMetering,
		isSupportNetMetering,
	} from '../Chart/chart_net_metering_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { getDeviceAvatarDefaultConfig } from 'src/helpers/device_state_view_helpers';
	// Design system components
	import SelectList from 'src/components/SelectList/SelectList.svelte';
	import {
		Alert,
		Button,
		CircleAvatar,
		Modal,
		Text,
		Token,
		Wizard,
		WizardContainer,
		WizardContent,
		WizardItem,
	} from '@Shelly/shelly-design-system/index';
	// Types
	import type { Device } from 'src/types/Device';
	import type { CloudApiResponse } from 'src/types/responses/CloudApiResponse';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	$: statusSnapshot = $devices_status;
	export let showModal = false;

	const getWizardItems = () => {
		const wizardStep = [
			{
				title: $format('label_select_device', {
					default: 'Select device',
				}),
				subtitle: $format('label_select_device_net_metering_info', {
					default: 'Select the device to apply Net Metering settings',
				}),
			},
			{
				title: $format('text_net_metering_period', {
					default: 'Net metering period',
				}),
				subtitle: $format('text_net_metering_period_info', {
					default: 'Configure the Net Metering period for the selected device',
				}),
			},
		];
		return wizardStep.map((item, i) => {
			return {
				...item,
				active: i === 0 ? true : false,
				stepNumber: i + 1,
				activeIndex: i,
			};
		});
	};

	let selectedDevices: Device[] = [];
	let wizardItems = getWizardItems();
	let wizardStep = wizardItems[0];
	let waitForResponse = false;
	let selectedPeriod = 0;
	let showSuccessModal = false;
	let successInterval: NodeJS.Timeout;

	const netMeteringPeriods = getNetMeteringPeriods();
	const handleSelection = (dev: Device) => {
		if (selectedDevices.find((d) => d.id == dev.id)) {
			selectedDevices = selectedDevices.filter((d) => d.id != dev.id);
		} else {
			selectedDevices.push(dev);
			selectedDevices = selectedDevices; // Trigger reactivity
		}
		roomsFiltered = sortListByPosition(filterDevices(Object.values($devices), selectedDevices));
	};
	const sortListByPosition = (list: any) => {
		return list.sort((x: any, y: any) => {
			return sortBy(x, y, 'position');
		});
	};
	const saveNetMeteringPeriod = async (value) => {
		if (waitForResponse) return;
		waitForResponse = true;
		let toastTexts = {};
		try {
			const res: CloudApiResponse[] = await Promise.all(
				selectedDevices.map(({ id }) =>
					postReq('interface/device/set_cloud_options', {
						id,
						set: 'measure_net_energy',
						val: value,
					}),
				),
			);
			if (res.every((res) => res?.isok)) {
				toastTexts = {
					success: value
						? $format('device_net_metering_enabled_success_toast', {
								values: {
									period: value,
								},
								default: `Net Metering statistics have been enabled with a {period}-minute interval.`,
							})
						: $format('device_net_metering_disabled_toast', {
								default: "The device 'Net metering' statistics disabled.",
							}),
				};
				showSuccessModal = true;
				// Show success modal for 5 seconds and automatically navigate to the device's charts
				successInterval = setTimeout(goToDeviceNetMetering, 5000);
				onSettingsResponse(res, null, toastTexts);
			} else {
				toastTexts = {
					error: $format('device_net_metering_error_toast', {
						default: 'Failed to update Net Metering settings for one or more devices.',
					}),
				};
			}
		} catch (error) {
			console.error('Failed to set net metering period:', error);
			toastTexts = {
				error: $format('device_net_metering_error_toast', {
					default: 'Failed to update Net Metering settings for one or more devices.',
				}),
			};
			onSettingsResponse([], error, toastTexts);
		} finally {
			waitForResponse = false;
		}
	};
	const filterDevices: any = (devices: Device[], selectedDevices: Device[]) => {
		let filteredDevicesByRoom = {};
		devices
			.filter(
				(dev) =>
					!dev.shared && dev.channel === 0 && (isSupportNetMetering(dev) || isEM(dev?.type)),
			)
			.forEach((device: Device, _i) => {
				const roomID = device.room_id;
				const deviceData = {
					name: `${device.name}${
						!statusSnapshot?.[real_id(device.id)]?.online
							? `- ${capFirstChar($format('label_offline'))}`
							: ''
					}`,
					id: device.id,
					selected: selectedDevices.find((el) => el.id == device.id) != null,
					roomName: deriveRoomName(roomID),
					roomID: roomID,
					image: device.image ? device.image : undefined,
					backgroundColor: device.backgroundColor ? device.backgroundColor : undefined,
					icon: device.icon ? device.icon : undefined,
					disabled: !isSupportNetMetering(device),
					descriptiveText:
						is_gen_2(device) && !isSupportFWNetMetering(device)
							? $format('device_status_unsupported', {
									default: 'Firmware is not supported. Update required',
								})
							: '',
				};

				if (filteredDevicesByRoom[roomID]) {
					filteredDevicesByRoom[roomID].cards = [
						...filteredDevicesByRoom[roomID].cards,
						deviceData,
					];
				} else {
					filteredDevicesByRoom[roomID] = {
						cards: [deviceData],
						roomName: deriveRoomName(roomID),
						roomID: roomID,
					};
				}
			});
		return Object.values(filteredDevicesByRoom);
	};
	const onCloseModal = () => {
		clearInterval(successInterval);
		showSuccessModal = false;
		showModal = false;
		selectedDevices = [];
		wizardItems = getWizardItems();
		wizardStep = wizardItems[0];
		waitForResponse = false;
		selectedPeriod = 0;
	};
	const goToDeviceNetMetering = () => {
		clearInterval(successInterval);
		if (selectedDevices[0]?.id) {
			navigateToSetting(selectedDevices[0].id, 'charts');
		}
		onCloseModal();
	};
	$: roomsFiltered = (statusSnapshot, sortListByPosition(filterDevices(Object.values($devices), selectedDevices)));

	onDestroy(() => {
		clearInterval(successInterval);
	});
</script>

<Modal
	title={$format('feature_nm_name', {
		default: 'Net Metering Support',
	})}
	{showModal}
	on:close={onCloseModal}
>
	<Wizard
		slot="toolbar"
		activeStepNumber={wizardStep.activeIndex}
		stepsTotal={wizardItems.length}
		activeStepTitle={wizardStep.title}
	>
		{#each wizardItems as item, i (i)}
			<WizardItem
				on:click={() => {
					wizardStep = wizardItems[i];
				}}
				active={wizardStep.activeIndex === i}
				title={item.title}
				disabled={i == 1 && selectedDevices.length == 0}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/each}
	</Wizard>
	<div class:flex-overflow-hidden={false} slot="content">
		<WizardContainer activeStepIndex={wizardStep.activeIndex} adaptiveHeight={true}>
			{#each wizardItems as item, i (i)}
				<WizardContent active={wizardStep.activeIndex === i} class="pb-s">
					<Text element="h2" class="mb-s">
						{item.subtitle}
					</Text>
					{#if i == 0}
						{#if roomsFiltered?.length === 0}
							<Alert
								type="warning"
								class="mb-s"
								heading={$format('no_devices_nm_support_heading', {
									default: 'No devices with Net Metering support found',
								})}
								message={$format('feature_nm_devices_requirements_message', {
									default:
										'Device based Net Metering requires a <strong> Generation 2 and above </strong>, (PRO EM, Gen3 EM) device with <strong>firmware version 1.5.0 or newer</strong>.',
								})}
								isHeadingBold
							/>
						{:else if roomsFiltered?.length > 0}
							<Alert
								type="warning"
								class="mb-s"
								heading={$format('feature_nm_devices_requirements_heading', {
									default: 'Supported devices and versions',
								})}
								isHeadingBold
								message={$format('feature_nm_devices_requirements_message', {
									default:
										'Device based Net Metering requires a <strong> Generation 2 and above </strong>, (PRO EM, Gen3 EM) device with <strong>firmware version 1.5.0 or newer</strong>.',
								})}
							/>
							<Alert
								type="info"
								class="mb-m"
								heading={$format('feature_nm_chanel_requirements_heading', {
									default: 'Important details',
								})}
								isHeadingBold
								message={$format('feature_nm_chanel_requirements_message', {
									default:
										'Device based Net Metering is enabled per device. For multi-channel devices (e.g. Shelly Pro EM), the configuration applies to <strong>all channels</strong>.This option can be managed on any device, at any time through the <strong>Application Settings tab → Energy Calculation menu.</strong>',
								})}
							/>
							<SelectList
								title={$format('label_selected', {
									default: 'Selected:',
								})}
								variant="checkbox"
								titleHighlight={selectedDevices.length}
								list={roomsFiltered}
								on:click={(e) => {
									handleSelection(e.detail);
								}}
							/>
						{/if}
					{:else if i == 1}
						<Alert
							class="mb-s"
							type="info"
							heading={$format('feature_nm_name', {
								default: 'Net Metering Support',
							})}
							isHeadingBold
							message={$format('no_devices_nm_support_info2', {
								default:
									'Net Metering is supported on Shelly Energy Meter devices, generation 2 and above. This feature helps you better understand your energy consumption and production. Note that firmware version 1.5.0 or newer is required.',
							})}
						/>
						<div class="d-flex flex-wrap mt-s">
							{#each netMeteringPeriods as period}
								<Token
									id="token-1kmhxm9"
									size="m"
									title={period.title}
									class="mb-s mr-s"
									selected={period.value === selectedPeriod}
									on:click={() => (selectedPeriod = period.value)}
								/>
							{/each}
						</div>
					{/if}
				</WizardContent>
			{/each}
		</WizardContainer>
	</div>
	<div slot="footer" class="d-flex justify-content-end">
		{#if wizardStep.activeIndex > 0}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				icon="fas fa-chevron-left"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => {
					if (wizardStep.activeIndex > 0) {
						wizardStep = wizardItems[wizardStep.activeIndex - 1];
					}
				}}
			>
				{$format('button_back', { default: 'Back' })}
			</Button>
		{/if}
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			icon={wizardStep.activeIndex == wizardItems.length - 1 ? 'fas fa-save' : ''}
			rightIcon={(wizardStep.activeIndex == 0 && selectedDevices.length === 0) ||
			wizardStep.activeIndex == wizardItems.length - 1
				? ''
				: 'fas fa-chevron-right'}
			responsiveSize={{
				sm: 'm',
			}}
			loading={waitForResponse}
			on:click={() => {
				if (wizardStep.activeIndex == 0 && selectedDevices.length === 0) {
					onCloseModal();
				} else if (wizardStep.activeIndex == wizardItems.length - 1) {
					saveNetMeteringPeriod(selectedPeriod);
				} else {
					wizardStep = wizardItems[wizardStep.activeIndex + 1];
				}
			}}
		>
			{wizardStep.activeIndex == 0 && selectedDevices.length === 0
				? $format('button_close', { default: 'Close' })
				: wizardStep.activeIndex == wizardItems.length - 1
					? $format('button_save', { default: 'Save' })
					: $format('button_next', { default: 'Next' })}
		</Button>
	</div>
</Modal>

<Modal
	title={$format('feature_nm_name', {
		default: 'Net Metering Support',
	})}
	on:close={goToDeviceNetMetering}
	showModal={showSuccessModal}
>
	<svelte:fragment slot="content">
		<div class="d-flex justify-content-center mb-l">
			<CircleAvatar
				size="xxl"
				icon="fas fa-bolt text-color-success"
				borderStatus={{ icon: 'fa-solid fa-check', color: 'color-success' }}
			/>
		</div>
		<Alert
			type="success"
			heading={$format('net_metering_on_boarding_enabled_success_heading', {
				default: 'Device based Net Metering is enabled successfully.',
			})}
			message={$format('net_metering_on_boarding_enabled_success_message', {
				default:
					'The device will now start gathering net metering statistics. It may take up to 10 minutes to start recording data.',
			})}
		/>
	</svelte:fragment>
</Modal>
